import { useMemo } from 'react';
import dayjs from 'dayjs';
import { alpha, Box, Stack, Typography } from '@mui/material';
import { Link } from 'react-router';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { BlockBody, DashboardPeriod, getPeriodRange } from '@retail/dashboard/components';
import { useFetchCentralAssortmentsInfoItems } from '@retail/dashboard/data-access';
import { useDashboardTFunction } from '@retail/dashboard/i18n';
import { CentralAssortmentInfoDTO, TagCountDTO } from '@retail/dashboard/types';
import { mgPrisRoutes } from '@retail/mgpris/config';

const MAX_VISIBLE = 5;
const DATE_FORMAT = 'YYYY-MM-DD';
const COLUMN_WIDTH = 150;

type Metric = 'updated' | 'future';

const METRIC_TONE: Record<Metric, 'primary' | 'secondary'> = {
  updated: 'primary',
  future: 'secondary',
};

interface AssortmentGroup {
  tagName: string;
  updated: number;
  future: number;
}

const groupByAssortment = (dto: CentralAssortmentInfoDTO): AssortmentGroup[] => {
  const groups = new Map<string, AssortmentGroup>();

  const ensure = (tagName: string): AssortmentGroup => {
    let group = groups.get(tagName);
    if (!group) {
      group = { tagName, updated: 0, future: 0 };
      groups.set(tagName, group);
    }
    return group;
  };

  const apply = (tags: TagCountDTO[], key: Metric) => {
    tags.forEach(({ tagName, count }) => {
      ensure(tagName)[key] += count;
    });
  };

  apply(dto.newItemCount, 'updated');
  apply(dto.updatedItemCount, 'updated');
  apply(dto.futureItemCount, 'future');

  return Array.from(groups.values()).sort((a, b) => b.updated + b.future - (a.updated + a.future));
};

interface Props {
  period: DashboardPeriod;
}

export const CentralAssortmentsPanel = ({ period }: Props) => {
  const t = useDashboardTFunction();
  const { assortmentId } = useSelectedOrgUnit();

  const from = useMemo(() => getPeriodRange(period).from, [period]);

  const { data, isLoading, isError } = useFetchCentralAssortmentsInfoItems({
    assortmentId,
    from: from.format(DATE_FORMAT),
    to: dayjs().format(DATE_FORMAT),
    suspense: false,
    refetchOnWindowFocus: false,
  });

  const groups = useMemo(() => (data ? groupByAssortment(data) : []), [data]);

  const centralAssortmentsPath = mgPrisRoutes.centralAssortments.getFullLinkPath();
  const visibleGroups = groups.slice(0, MAX_VISIBLE);

  return (
    <BlockBody
      isLoading={isLoading}
      isError={isError}
      isEmpty={!isLoading && !isError && groups.length === 0}
      emptyMessage={t('dashboard.sentraleSortimenter.empty', {
        period: t(`dashboard.sentraleSortimenter.periodNoun.${period}`),
      })}
    >
      <Stack gap={0.25}>
        <Box
          sx={{
            px: 1,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0, flexGrow: 1 }} />
          <Stack direction="row" gap={1} sx={{ flexShrink: 0 }}>
            <ColumnHeader
              label={t('dashboard.updatedItems')}
              caption={t('dashboard.sentraleSortimenter.periodCaption', {
                period: t(`dashboard.sentraleSortimenter.periodNoun.${period}`),
              })}
              metric="updated"
            />
            <ColumnHeader
              label={t('dashboard.futureItems')}
              caption={t('dashboard.sentraleSortimenter.futureCaption')}
              metric="future"
            />
          </Stack>
        </Box>

        {visibleGroups.map((group) => (
          <GroupRow key={group.tagName} group={group} to={centralAssortmentsPath} />
        ))}
      </Stack>
    </BlockBody>
  );
};

const GroupRow = ({ group, to }: { group: AssortmentGroup; to: string }) => (
  <Box
    component={Link}
    to={to}
    sx={{
      textDecoration: 'none',
      color: 'text.primary',
      borderRadius: 1.5,
      px: 1,
      py: 0.75,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      transition: 'background-color 120ms',
      '&:hover': { backgroundColor: 'action.hover' },
    }}
  >
    <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0, fontWeight: 600 }}>
      {group.tagName}
    </Typography>
    <Stack direction="row" gap={1} sx={{ flexShrink: 0 }}>
      <MetricValue value={group.updated} metric="updated" />
      <MetricValue value={group.future} metric="future" />
    </Stack>
  </Box>
);

const MetricValue = ({ value, metric }: { value: number; metric: Metric }) => {
  const tone = METRIC_TONE[metric];
  const isZero = value === 0;

  return (
    <Box sx={{ width: COLUMN_WIDTH, display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          minWidth: 44,
          px: 1,
          py: 0.25,
          borderRadius: 1,
          textAlign: 'center',
          backgroundColor: (theme) =>
            isZero
              ? 'transparent'
              : theme.vars
              ? `rgba(${theme.vars.palette[tone].mainChannel} / 0.1)`
              : alpha(theme.palette[tone].main, 0.1),
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ color: isZero ? 'text.disabled' : `${tone}.main` }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const ColumnHeader = ({
  label,
  caption,
  metric,
}: {
  label: string;
  caption: string;
  metric: Metric;
}) => (
  <Stack alignItems="center" gap={0.25} sx={{ width: COLUMN_WIDTH, px: 1 }}>
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Box
        sx={{
          width: 8,
          height: 8,
          flexShrink: 0,
          borderRadius: '50%',
          backgroundColor: `${METRIC_TONE[metric]}.main`,
        }}
      />
      <Typography variant="caption" color="text.secondary" align="center" sx={{ lineHeight: 1.2 }}>
        {label}
      </Typography>
    </Stack>
    <Typography variant="caption" color="text.disabled" align="center" sx={{ lineHeight: 1.2 }}>
      {caption}
    </Typography>
  </Stack>
);
